import styles from '../Theme/Styles/telephonyBtns.styles.css'
import React, { useEffect, useState } from 'react'
import {
  ActionButton,
  AlertDialog,
  DialogContainer,
  Flex,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum'
import {
  Hold,
  End,
  Join,
  Mute,
  Swap,
  DialogIcon,
  Transfer,
  UnMute
} from '../Theme/Icons/DialpadIcons'
import localization from '../lang/localization'
import CallButtonsPopup from './EndCallPopup'

type propsData = {
  lang: any
  close: any
  callStatus: string
  isHold: boolean
  isMute: boolean
  isJoin: boolean
  isSwap: boolean
  isTransfer: boolean
  currentContactId: string
  callConnectionCount: number
  currentQueueName: string
  transferToQueues: any
  callBackEvent: (eventType: string, eventData: object) => void
  closePopup: (close: any, clear: boolean, transferType: string) => void
  callBackEventToolBar: (eventType: string, eventData: object) => void
}

type ActionKeys =
  | 'end'
  | 'hold'
  | 'mute'
  | 'dialog'
  | 'join'
  | 'swap'
  | 'transfer'

const DialpadButtons = React.memo((props: propsData) => {
  const {
    lang,
    close,
    callStatus,
    isHold,
    isMute,
    isJoin,
    isSwap,
    isTransfer,
    currentContactId,
    callConnectionCount,
    transferToQueues,
    currentQueueName,
    callBackEvent,
    closePopup
  } = props

  const [isOpen, setOpen] = useState(false)
  const [action, setAction] = useState({
    end: { disabled: false },
    hold: { click: false, disabled: false },
    mute: { click: false, disabled: false },
    dialog: { click: false, disabled: false },
    join: { click: false, disabled: false },
    swap: { click: false, disabled: true },
    transfer: { click: false, disabled: false }
  })
  const [
    showReturnToQueueConfirmation,
    setShowReturnToQueueConfirmation
  ] = useState(false)

  const onCallAction = (e: any) => {
    const event = e.target.id as ActionKeys

    // if (['mute', 'hold'].includes(event)) {
    if (event === 'mute' || event === 'hold') {
      const val = !action?.[event].click
      callBackEvent(`${event}call`, { [event]: val })
    }
    if (event === 'transfer') {
      const nextTransferVal = !action.transfer.click

      setAction((prevAction: any) => ({
        ...prevAction,
        transfer: {
          ...prevAction.transfer,
          click: nextTransferVal,
          disabled: !callStatus || callStatus === 'Closed'
        }
      }))
      callBackEvent('TRANSFER', { transfer: true })
    }
    if (event === 'join') {
      callBackEvent('JOIN_CALL', {})
    }
    if (event === 'swap') {
      callBackEvent('SWAP_CALL', {})
    }
    if (event === 'dialog') {
      callBackEvent('DIALOG_CLICK', {})
    }
  }

  useEffect(() => {
    const status = !callStatus || callStatus === 'Closed' || false
    setAction((prevAction: any) => ({
      ...prevAction,
      end: {
        ...prevAction.end,
        disabled: callConnectionCount ? false : status
      },
      mute: {
        ...prevAction.mute,
        click: isMute || false,
        disabled: status
      },
      hold: {
        ...prevAction.hold,
        click: isHold || false,
        disabled: status
      },
      join: {
        ...prevAction.join,
        click: isJoin || false,
        disabled: status || isJoin
      },
      swap: {
        ...prevAction.swap,
        click: isSwap || false,
        disabled: status || !isTransfer || isJoin
      },
      transfer: {
        ...prevAction.transfer,
        click: isTransfer || false,
        disabled: status || isTransfer
      }
    }))
  }, [
    isHold,
    isMute,
    currentContactId,
    callStatus,
    isJoin,
    isSwap,
    isTransfer,
    callConnectionCount
  ])

  const handleClose = (clear: boolean) => {
    if (clear) {
      closePopup(close, true, 'voice')
      callBackEvent('endcall', {})
    }
    setOpen(false)
  }

  const executeReturnToQueue = () => {
    const quickConnectList = transferToQueues?.Queue?.filter(
      (queue: any) => queue?.name === currentQueueName
    )
    const quickConnectId = quickConnectList[0]?.endpointARN
    const eventData = {
      message: '',
      option: 'Queue',
      overrideSuggestion: '',
      queue: {
        endpointARN: quickConnectId,
        endpointId: quickConnectId,
        type: 'queue',
        name: currentQueueName,
        phoneNumber: null,
        agentLogin: null,
        queue: null,
        status: 'Available'
      }
    }
    props.callBackEventToolBar('COLD_TRANSFER_CALL', eventData)
  }

  return (
    <Flex UNSAFE_className={styles['call-action-container']}>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        {callStatus === 'Active' || callConnectionCount ? (
          <div>
            <TooltipTrigger>
              <ActionButton
                UNSAFE_className={styles['telephony-end']}
                isDisabled={action.end.disabled}
                onPress={() => setOpen(true)}
              >
                <End />
              </ActionButton>
              <Tooltip>{localization[lang].TELEPHONY.END}</Tooltip>
            </TooltipTrigger>
            <DialogContainer isDismissable onDismiss={() => setOpen(false)}>
              {isOpen && (
                <CallButtonsPopup handleClose={handleClose} lang={lang} />
              )}
            </DialogContainer>
          </div>
        ) : (
          <ActionButton
            UNSAFE_className={styles['telephony-end']}
            isDisabled={action.end.disabled}
            onPress={close}
          >
            <End />
          </ActionButton>
        )}
      </Flex>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        <TooltipTrigger>
          <ActionButton
            id='hold'
            UNSAFE_className={`${
              action.hold.click
                ? styles['telephony-hold-clicked']
                : styles['telephony-hold']
            }`}
            isDisabled={action.hold.disabled}
            onPress={onCallAction}
          >
            <Hold />
          </ActionButton>
          <Tooltip>
            {action.hold.click
              ? localization[lang].TELEPHONY.UNHOLD
              : localization[lang].TELEPHONY.HOLD}
          </Tooltip>
        </TooltipTrigger>
      </Flex>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        <TooltipTrigger>
          <ActionButton
            id='mute'
            UNSAFE_className={`${
              action.mute.click
                ? styles['telephony-mute-clicked']
                : styles['telephony-mute']
            }`}
            isDisabled={action.mute.disabled}
            onPress={onCallAction}
          >
            {action.mute.click ? <UnMute /> : <Mute />}
          </ActionButton>

          <Tooltip>
            {action.mute.click
              ? localization[lang]?.TELEPHONY.UNMUTE
              : localization[lang]?.TELEPHONY.MUTE}
          </Tooltip>
        </TooltipTrigger>
        {/* <Text UNSAFE_className={styles['telephony-icon-text']}>{localization[lang].TELEPHONY.MUTE}</Text> */}
      </Flex>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        <TooltipTrigger>
          <ActionButton
            id='dialog'
            UNSAFE_className={styles['telephony-dialog']}
            isDisabled={action.dialog.disabled}
            onPress={onCallAction}
          >
            <DialogIcon />
          </ActionButton>
          <Tooltip>{localization[lang].TELEPHONY.DIALPAD}</Tooltip>
        </TooltipTrigger>
      </Flex>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        <TooltipTrigger>
          <ActionButton
            id='join'
            isDisabled={action.join.disabled}
            UNSAFE_className={styles['telephony-dialog']}
            onPress={onCallAction}
          >
            <Join />
          </ActionButton>
          <Tooltip>{localization[lang].TELEPHONY.JOIN}</Tooltip>
        </TooltipTrigger>
      </Flex>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        <TooltipTrigger>
          <ActionButton
            id='swap'
            isDisabled={action.swap.disabled}
            UNSAFE_className={styles['telephony-dialog']}
            onPress={onCallAction}
          >
            <Swap />
          </ActionButton>
          <Tooltip>{localization[lang].TELEPHONY.SWAP}</Tooltip>
        </TooltipTrigger>
      </Flex>
      <Flex UNSAFE_className={styles['call-action-subcontiner']}>
        <TooltipTrigger>
          <ActionButton
            id='transfer'
            isDisabled={action.transfer.disabled}
            UNSAFE_className={styles['telephony-dialog']}
            onPress={() => setShowReturnToQueueConfirmation(true)}
          >
            <Transfer />
          </ActionButton>
          <Tooltip>{localization[lang].TRANSFER_CALL.TRANSFER}</Tooltip>
        </TooltipTrigger>
        <DialogContainer
          onDismiss={() => setShowReturnToQueueConfirmation(false)}
          {...props}
        >
          {showReturnToQueueConfirmation && (
            <AlertDialog
              variant='confirmation'
              title='Return to queue confirmation'
              primaryActionLabel='Return to Queue'
              cancelLabel='Cancel'
              autoFocusButton='primary'
              onPrimaryAction={() => executeReturnToQueue()}
              onCancel={() => setShowReturnToQueueConfirmation(false)}
            >
              Are you sure to return this call back to the queue?
            </AlertDialog>
          )}
        </DialogContainer>
      </Flex>
    </Flex>
  )
})

export default DialpadButtons
